/**
 * Store: Overlays
 * -----------------------------------------------------------------------------
 * Drawers, modals, and shelves.
 * - Tied to the overlay component.
 * - Only JSDoc custom parameters, ignore module state or object.
 *
 */

/**
 * @typedef {Object} Overlay
 * @property {Function} [callback] - Callback function to run on close.
 * @property {Boolean} [clearQueue] - Clears other overlays from queue.
 * @property {String|Array} [component] - Component handle, or array of
 * component handles to update state to `loading` when overlay opens, updated in
 * the index component.
 * @property {Object} [focusElements] - Additional focus trap elements.
 * @property {Array} [focusElements.containers] - Array of additional elements
 * to search inside for focusable elements for the focus trap.
 * @property {Array} [focusElements.elements] - Array of additional elements to
 * include in the focus trap.
 * @property {Boolean} [focusElements.prepend] - By default the order of
 * elements in the focus trap is overlay > containers > elements, to prepend the
 * containers and elements so the order is elements > containers > overlay then
 * set this to true.
 * @property {Boolean} [ignoreDismissed] - Open even if already dismissed.
 * @property {Number} [index] - Automatically generated, do not manually pass,
 * overlay's index in the active array, -1 if not in the array, passed to
 * mutations to keep them simple.
 * @property {String} namespace - Namespace of overlay.
 * @property {Boolean} [queue] - If the overlay should be queued.
 * @property {Boolean} [stuck] - If the overlay should be stuck when it is
 * active, meaning it cannot be closed until stuck is set to false.
 */
import { defineStore } from 'pinia';

const useOverlaysStore = defineStore('overlays', {
  state: () => ({
    active: [],
    dismissed: [],
  }),

  getters: {
    /**
     * Get the current active overlay's object.
     * @returns {Overlay}
     */
    getActiveOverlay(state) {
      return state.active[0];
    },

    /**
     * Get overlay object by namespace or object.
     * @param {String|Object} arg - Overlay namespace or object.
     * @returns {Overlay}
     */
    getOverlay: (state) => {
      return (arg) => {
        const namespace = typeof arg === 'object' ? arg.namespace : arg;
        return state.active.find((overlay) => overlay.namespace === namespace);
      };
    },

    /**
     * Get if the overlay namespace has been dismissed.
     * - Returns false if ignoreDismissed is true.
     * - Or if theme settings have disabled overlays.
     * @param {Overlay} overlay - Overlay object.
     * @returns {Boolean}
     */
    getOverlayDismissed: (state) => {
      return (overlay) => {
        const matchesDismissed = state.dismissed.find((dismissed) => {
          return dismissed === overlay.namespace;
        });

        return (
          matchesDismissed &&
          !overlay.ignoreDismissed &&
          !cnvs.settings.disableOverlaysDismissed
        );
      };
    },

    /**
     * Get overlay index by namespace or object.
     * @param {String|Object} arg - Overlay namespace or object.
     * @returns {Number}
     */
    getOverlayIndex: (state) => {
      return (arg) => {
        const namespace = typeof arg === 'object' ? arg.namespace : arg;
        return state.active.findIndex(
          (overlay) => overlay.namespace === namespace,
        );
      };
    },
  },

  actions: {
    /**
     * Close overlay.
     * - Closes first active overlay if no namespace is provided.
     * - Does nothing if provided namespace does not match active overlays or
     *   active/provided overlay is stuck.
     * - This behaviour means that a queued overlay can be closed even if the
     *   active overlay is stuck.
     * @param {Object|String} [argument] - Overlay namespace to close, or an
     * object of options
     * @param {Boolean} [argument.dismiss] - Add overlay to dismissed state.
     * @param {Boolean|String} [argument.namespace] - Overlay namespace to
     * close, pass false to close active overlay if you need to set options.
     * @param {String} [argument.type] - Type of close to pass to the callback.
     */
    close(argument) {
      const namespace = argument?.namespace || argument;
      const dismiss = argument?.dismiss || true;

      const overlay = namespace
        ? this.getOverlay(namespace)
        : this.getActiveOverlay;

      if ((namespace && !overlay) || overlay.stuck) {
        return;
      }

      const index = this.getOverlayIndex(namespace);

      if (dismiss) {
        const dismissedOverlay = namespace
          ? this.active[index]
          : this.active[0];

        this.setDismissed(dismissedOverlay?.namespace);
      }

      this.setClose(index);
      this.closeCallback({ overlay, type: argument?.type });
    },

    /**
     * Closes all overlays.
     * - If active overlay is stuck then action is ignored.
     * - Only the current active overlay is added to dismissed.
     * @param {Object} [options] - Close options.
     * @param {Boolean} [options.dismiss] - Add overlay to dismissed state.
     * @param {String} [options.type] - Type of close to pass to the callback.
     */
    closeAll({ dismiss = true, type } = {}) {
      const overlay = this.active[0];

      if (overlay?.stuck) {
        return;
      }

      if (overlay && dismiss) {
        this.setDismissed(overlay.namespace);
      }

      this.active = [];
      this.closeCallback({ overlay, type });
    },

    /**
     * Run close callback.
     * - Used in close and close call actions.
     * @param {Object} options - Close callback options.
     * @param {Object} options.overlay - Overlay object.
     * @param {String} [options.type] - Type of close to pass to the callback.
     */
    closeCallback({ overlay, type = 'button' } = {}) {
      cnvs.EventBus.emit('overlay:closed', overlay);

      if (!overlay.callback || typeof overlay.callback !== 'function') {
        return;
      }

      overlay.callback(type);
    },

    /**
     * Open overlay.
     * - Only open if not dismissed, or ignoredDismissed is true.
     * - If active overlay is stuck then queue new overlay.
     * @param {Overlay} overlay - Overlay object.
     */
    open(overlay) {
      if (this.getOverlayDismissed(overlay)) {
        return;
      }

      const index = this.getOverlayIndex(overlay);

      if (this.getActiveOverlay?.stuck) {
        this.setOpen({ ...overlay, index, queue: true });
        return;
      }

      this.setOpen({ ...overlay, index });
    },

    /**
     * Queues overlay.
     * @param {Overlay} overlay - Overlay object.
     */
    queue(overlay) {
      this.open({ ...overlay, queue: true });
    },

    /**
     * Toggle overlay.
     * - Close if overlay is already active.
     * - Otherwise make overlay active.
     * @param {Overlay} overlay - Overlay object.
     */
    toggle(overlay) {
      if (this.getOverlayIndex(overlay) > -1) {
        this.close(overlay.namespace);
        return;
      }

      this.open(overlay);
    },

    /**
     * Update existing overlay object with new data.
     * @param {String} namespace - Overlay namespace.
     * @param {Object} update - Updated properties.
     */
    update({ namespace, update } = {}) {
      const index = this.getOverlayIndex(namespace);

      if (index < 0) {
        return;
      }

      const overlay = this.getOverlay(namespace);

      /**
       * Namespace, component, and index are set after object spreads to prevent
       * them being modified.
       */
      this.active[overlay.index] = {
        ...overlay,
        ...update,
        namespace: overlay.namespace,
        component: overlay.component,
        index,
      };
    },
    /**
     * Closes overlay at active array index.
     * @param {Number} index - Index of overlay in active array to remove, if -1
     * then remove the first overlay in active array.
     */
    setClose(index) {
      const mutateIndex = index > 0 ? index : 0;
      this.active.splice(mutateIndex, 1);
    },
    /**
     * Set dismissed state.
     * @param {String} namespace - Namespace to add to dismissed.
     */
    setDismissed(namespace) {
      if (!namespace || this.dismissed.includes(namespace)) {
        return;
      }
      this.dismissed.push(namespace);
    },
    /**
     * Add overlay to active array.
     * @param {Overlay} overlay - Overlay object.
     */
    setOpen(overlay) {
      if (overlay.clearQueue) {
        this.active = [overlay];
        return;
      }
      /**
       * If existing overlay and queued then update.
       */
      if (overlay.index > -1 && overlay.queue) {
        this.active[overlay.index] = overlay;
        return;
      }
      /**
       * If not existing and queueing then add to end.
       */
      if (overlay.queue) {
        this.active.push(overlay);
        return;
      }
      /**
       * Remove item if existing and not queueing.
       */
      if (overlay.index && overlay.index > -1) {
        this.active.splice(overlay.index, 1);
      }
      this.active.unshift(overlay);
    },
  },
  persist: {
    pick: ['dismissed'],
  },
});

export default useOverlaysStore;
